import { useEffect } from 'react';
import { ExportFileType } from 'common/types/export-version-file';
import { progressPartMapProps } from '@/types/playControl';
import { useTaskEditorContext } from '@/context/TaskEditorContext';

export const getTransform = DOM => {
  let arr = getComputedStyle(DOM).transform.split(',');
  return {
    transX: isNaN(+arr[arr.length - 2]) ? 0 : +arr[arr.length - 2], // 获取translateX
    transY: isNaN(+arr[arr.length - 1].split(')')[0]) ? 0 : +arr[arr.length - 1].split(')')[0], // 获取translateX
    scale: isNaN(parseFloat(arr[3])) ? 1 : parseFloat(arr[3]), // 获取图片缩放比例
  };
};
export const limitBorder = (
  innerDOM: HTMLElement,
  outerDOM: HTMLElement,
  moveX: number,
  moveY: number,
  scale: number,
) => {
  let { clientWidth: innerWidth, clientHeight: innerHeight, offsetLeft: innerLeft, offsetTop: innerTop } = innerDOM;
  let { clientWidth: outerWidth, clientHeight: outerHeight } = outerDOM;
  let transX;
  let transY;
  // 放大的图片超出box时 图片最多拖动到与边框对齐
  if (innerWidth * scale > outerWidth || innerHeight * scale > outerHeight) {
    if (innerWidth * scale > outerWidth && innerWidth * scale > outerHeight) {
      transX = Math.min(
        Math.max(moveX, outerWidth - (innerWidth * (scale + 1)) / 2 - innerLeft),
        -innerLeft + (innerWidth * (scale - 1)) / 2,
      );
      transY = Math.min(
        Math.max(moveY, outerHeight - (innerHeight * (scale + 1)) / 2 - innerTop),
        -innerTop + (innerHeight * (scale - 1)) / 2,
      );
    } else if (innerWidth * scale > outerWidth && !(innerWidth * scale > outerHeight)) {
      transX = Math.min(
        Math.max(moveX, outerWidth - (innerWidth * (scale + 1)) / 2 - innerLeft),
        -innerLeft + (innerWidth * (scale - 1)) / 2,
      );
      transY = Math.max(
        Math.min(moveY, outerHeight - (innerHeight * (scale + 1)) / 2 - innerTop),
        -innerTop + (innerHeight * (scale - 1)) / 2,
      );
    } else if (!(innerWidth * scale > outerWidth) && innerWidth * scale > outerHeight) {
      transX = Math.max(
        Math.min(moveX, outerWidth - (innerWidth * (scale + 1)) / 2 - innerLeft),
        -innerLeft + (innerWidth * (scale - 1)) / 2,
      );
      transY = Math.min(
        Math.max(moveY, outerHeight - (innerHeight * (scale + 1)) / 2 - innerTop),
        -innerTop + (innerHeight * (scale - 1)) / 2,
      );
    }
  } else {
    transX = Math.max(
      Math.min(moveX, outerWidth - (innerWidth * (scale + 1)) / 2 - innerLeft),
      -innerLeft + (innerWidth * (scale - 1)) / 2,
    );
    transY = Math.max(
      Math.min(moveY, outerHeight - (innerHeight * (scale + 1)) / 2 - innerTop),
      -innerTop + (innerHeight * (scale - 1)) / 2,
    );
  }

  return { transX, transY };
};

let partList, duration, timeRange;
const progressPartMap: Record<number, progressPartMapProps> = {};
export const handleVideoProgressEvent = videoRef => {
  const playerBarProgressRef = document.getElementById('player-bar-progress') as HTMLDivElement;
  if (!playerBarProgressRef) return;

  duration = videoRef?.current?.duration || 0;
  timeRange = videoRef?.current?.buffered || 0;

  partList = document.querySelectorAll(`.player-bar-progress-part`);
  for (let i = 0; i <= timeRange.length - 1; i++) {
    let start = timeRange.start(i);
    let end = timeRange.end(i);

    progressPartMap[i] = {
      left: (start / duration) * 100,
      width: ((end - start) / duration) * 100,
    };
    if (!partList[i]) {
      let div = document.createElement('div');
      div.setAttribute('class', `player-bar-progress-part`);
      div.style.setProperty('left', progressPartMap[i].left + '%');
      div.style.setProperty('width', progressPartMap[i].width + '%');
      playerBarProgressRef.appendChild(div);
    } else {
      (partList[i] as HTMLDivElement).style.left = progressPartMap[i].left + '%';
      (partList[i] as HTMLDivElement).style.width = progressPartMap[i].width + '%';
    }
  }
};

export const onVideoFinish = (videoRef, fn) => {
  if (videoRef && videoRef.current) {
    duration = videoRef?.current?.duration;
    timeRange = videoRef?.current?.buffered;
    if (timeRange?.length < 1) return;
    if (duration && timeRange) {
      const start = timeRange.start(0);
      const end = timeRange.end(0);
      const percent = (end - start) / duration;

      if (timeRange.length == 1 && percent === 1) {
        fn();
        return true;
      }
    }
  }

  return false;
};

export const useVideoLoading = (videoRef, type) => {
  const { setFileLoading } = useTaskEditorContext();

  useEffect(() => {
    const handleSeeking = () => {};

    const handleTimeupdate = () => {};
    const handleSeeked = () => {
      setFileLoading(false);
    };
    if (type === ExportFileType.Video) {
      videoRef.current?.addEventListener('seeking', handleSeeking);
      videoRef.current?.addEventListener('seeked', handleSeeked);
      // videoRef.current?.addEventListener('timeupdate', handleTimeupdate);

      return () => {
        videoRef.current?.removeEventListener('seeking', handleSeeking);
        videoRef.current?.removeEventListener('seeked', handleSeeked);
        // videoRef.current?.removeEventListener('timeupdate', handleTimeupdate);
      };
    }
  }, [videoRef.current]);
};

export const useVideoAvoidStuck = (videoRef, totalFrames) => {
  const MAXEXCEED = 500; // .5s

  let lastTime, perTime, currentTime;
  const handleTimeupdate = () => {
    if (videoRef.current?.readyState < videoRef.current?.HAVE_FUTURE_DATA) {
      perTime = videoRef?.current?.duration / totalFrames;

      if (lastTime) clearTimeout(lastTime);
      lastTime = setTimeout(() => {
        currentTime = videoRef?.current?.currentTime + perTime;
        const isLegal = !isNaN(currentTime) && currentTime > 0;
        isLegal && Object.assign(videoRef.current, { currentTime });
        lastTime = null;
      }, MAXEXCEED);
    }
  };

  useEffect(() => {
    videoRef.current?.addEventListener('timeupdate', handleTimeupdate);
    return () => {
      videoRef.current?.removeEventListener('timeupdate', handleTimeupdate);
    };
  }, []);
};
